<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS, SCHEDULE_MESSAGES } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import GroupInfo from './contact/GroupInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import Draggable from 'vuedraggable';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import ScheduleMessageModal from 'dashboard/components/widgets/conversation/bubble/ScheduleMessageModal.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);
const scheduledMessages = ref([]);
const isScheduleModalOpen = ref(false);
const isLoadingScheduledMessages = ref(false);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const isSchedulingFeatureEnabled = computed(
  () =>
    isCloudFeatureEnabled(FEATURE_FLAGS.SCHEDULING_MESSAGES) ||
    isCloudFeatureEnabled(SCHEDULE_MESSAGES)
);

const scheduledMessagesCount = computed(() => {
  if (isLoadingScheduledMessages.value) return null;
  const pendingMessages = scheduledMessages.value.filter(
    message => message.status === 'pending'
  );
  return pendingMessages.length;
});

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);
const isGroupConversation = computed(() => currentChat.value.group === true);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value) || {});
const agentsList = useMapGetter('agents/getAgents');
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const responsibleAgentId = computed(() => {
  const id = contactAdditionalAttributes.value.responsible_agent_id;
  return id ? Number(id) : null;
});

const responsibleAgentName = computed(() => {
  if (!responsibleAgentId.value) {
    return '';
  }
  const agent = agentsList.value.find(
    item => item.id === responsibleAgentId.value
  );
  return agent?.name || '';
});

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const fetchScheduledMessages = async () => {
  if (
    !conversationId.value ||
    !isSchedulingFeatureEnabled.value ||
    isGroupConversation.value
  ) {
    return;
  }
  isLoadingScheduledMessages.value = true;
  try {
    const response = await ConversationApi.getScheduledMessages(
      conversationId.value
    );
    scheduledMessages.value = response.data?.payload || [];
  } catch (error) {
    useAlert(
      error?.message ||
        'Não foi possível carregar as mensagens agendadas neste momento.'
    );
    scheduledMessages.value = [];
  } finally {
    isLoadingScheduledMessages.value = false;
  }
};

watch(conversationId, (newId, prevId) => {
  if (newId && newId !== prevId) {
    fetchScheduledMessages();
  }
});

const openScheduleModal = () => {
  isScheduleModalOpen.value = true;
};

const closeScheduleModal = () => {
  isScheduleModalOpen.value = false;
};

watch(conversationSidebarItemsOrder, newOrder => {
  conversationSidebarItems.value = newOrder;
});

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
  fetchScheduledMessages();
});
</script>

<template>
  <div class="w-full">
    <SidebarActionsHeader
      :title="
        isGroupConversation
          ? $t('CONVERSATION.SIDEBAR.GROUP')
          : $t('CONVERSATION.SIDEBAR.CONTACT')
      "
      @close="closeContactPanel"
    />
    <GroupInfo
      v-if="isGroupConversation"
      :conversation="currentChat"
      :contact="contact"
    />
    <ContactInfo
      v-else
      :contact="contact"
      :channel-type="channelType"
      :show-schedule-action="isSchedulingFeatureEnabled"
      @open-schedule="openScheduleModal"
    />
    <div
      v-if="isSchedulingFeatureEnabled && !isGroupConversation"
      class="flex items-center px-4 py-3 text-sm text-n-slate-11"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-calendar text-n-slate-10" />
        <span>
          <template v-if="scheduledMessagesCount === null">
            {{ $t('GENERAL.LOADING') }}
          </template>
          <template v-else-if="scheduledMessagesCount === 0">
            {{ $t('CONVERSATION_SIDEBAR.SCHEDULE.NO_MESSAGES') }}
          </template>
          <template v-else>
            {{
              $t('CONVERSATION_SIDEBAR.SCHEDULE.PENDING', {
                count: scheduledMessagesCount,
              })
            }}
          </template>
        </span>
      </div>
    </div>
    <div class="px-2 pb-8 list-group">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'responsible_agent' && !isGroupConversation
            "
          >
            <AccordionItem
              :title="$t('CONTACT_PANEL.SIDEBAR_SECTIONS.RESPONSIBLE_AGENT')"
              :is-open="isContactSidebarItemOpen('is_responsible_agent_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_responsible_agent_open', value)
              "
            >
              <div class="px-4 py-2 text-sm text-n-slate-12">
                {{
                  responsibleAgentName ||
                  $t('CONVERSATION_SIDEBAR.SELECT.PLACEHOLDER')
                }}
              </div>
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
    <ScheduleMessageModal
      v-if="!isGroupConversation"
      :show="isScheduleModalOpen"
      :conversation-id="conversationId"
      @close="closeScheduleModal"
      @updated="fetchScheduledMessages"
    />
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply pb-3 border-b border-solid border-n-weak;
  }

  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      @apply box-border pl-6;
    }

    .multiselect__element {
      span {
        @apply w-full;
      }
    }
  }
}
</style>
